// Editor state for the Workflow Builder: the workflow definition (with
// undo/redo), the uploaded sample files (never saved to the server) and the
// current selection. Undo/redo covers steps, outputs and workflow settings;
// sources (files and their settings) are kept as they are.
import { CODE_STEP, getStepDefinition, stepInputs } from "../../../engine";

const HISTORY_LIMIT = 50;

export const emptyWorkflow = (projectID) => ({
  projectID,
  name: "Untitled workflow",
  description: "",
  visibility: "private",
  sources: [],
  steps: [],
  outputs: [],
});

export const initialState = {
  workflow: null,
  // { [sourceId]: { key, fileName, sheets: [{ name, rowCount, columnCount }], sheetName, columnOverrides? } }
  // The rows stay in the data worker under `key` (see shared/sourceFiles.js).
  files: {},
  selectedId: null,
  past: [],
  future: [],
  dirty: false,
};

// Smallest unused id like "step3".
export const nextId = (prefix, taken) => {
  const used = new Set(taken);
  let n = 1;
  while (used.has(`${prefix}${n}`)) n++;
  return `${prefix}${n}`;
};

// Default output name: "output file N" with the lowest unused N.
const nextOutputName = (outputs) => {
  const taken = new Set(outputs.map((o) => (o.fileName || "").trim().toLowerCase()));
  let n = 1;
  while (taken.has(`output file ${n}`)) n++;
  return `output file ${n}`;
};

// Removes a step; whatever read from it reads the step's (first) input instead.
export const removeStep = (workflow, id) => {
  const index = workflow.steps.findIndex((s) => s.id === id);
  if (index === -1) return workflow;
  const replacement = stepInputs(workflow, index)[0];
  const relink = (inputs) => [...new Set(inputs.map((input) => (input === id ? replacement : input)).filter(Boolean))];
  const steps = workflow.steps.filter((s) => s.id !== id);
  const rewired = steps.map((step, i) => {
    if (Array.isArray(step.inputs)) return step.inputs.includes(id) ? { ...step, inputs: relink(step.inputs) } : step;
    // The next step read the deleted one implicitly; keep it on the same table.
    if (i === index && replacement && stepInputs({ ...workflow, steps }, i)[0] !== replacement) return { ...step, inputs: [replacement] };
    return step;
  });
  const outputs = workflow.outputs.map((output) =>
    (output.sheets || []).some((sheet) => sheet.from === id)
      ? { ...output, sheets: output.sheets.map((sheet) => (sheet.from === id ? { ...sheet, from: replacement } : sheet)) }
      : output,
  );
  return { ...workflow, steps: rewired, outputs };
};

export const allIds = (workflow) => [
  ...workflow.sources.map((s) => s.id),
  ...workflow.steps.map((s) => s.id),
  ...workflow.outputs.map((o) => o.id),
];

// Roadmap 1: files held raw cells on the UI thread (still used by its tests).
export const cellsOf = (file) => file?.sheets.find((sheet) => sheet.name === file.sheetName)?.cells ?? file?.sheets[0]?.cells;

// Default params for a new step: each parameter's default value.
const defaultParams = (type) =>
  Object.fromEntries(
    Object.entries(getStepDefinition(type)?.params || {})
      .filter(([, field]) => field.default !== undefined)
      .map(([key, field]) => [key, JSON.parse(JSON.stringify(field.default))]),
  );

// Applies a change to the workflow and records undo history.
const change = (state, workflow, extra = {}) => ({
  ...state,
  ...extra,
  workflow,
  past: [...state.past.slice(-HISTORY_LIMIT + 1), state.workflow],
  future: [],
  dirty: true,
});

// Applies a change to sources only; not recorded in undo history.
const sourceChange = (state, workflow, extra = {}) => ({ ...state, ...extra, workflow, dirty: true });

// Restores an undo/redo snapshot but keeps the current sources. A first step
// that read a since-removed source by default stays pointed at it.
const restore = (snapshot, current) => {
  const [first, ...rest] = snapshot.steps;
  const defaultSource = snapshot.sources[0]?.id;
  const lost = first && !Array.isArray(first.inputs) && defaultSource && !current.sources.some((s) => s.id === defaultSource);
  return { ...snapshot, sources: current.sources, steps: lost ? [{ ...first, inputs: [defaultSource] }, ...rest] : snapshot.steps };
};

// Ids that steps or outputs read, here and in undo history. New sources never
// reuse them, so a step can't silently reconnect to a different file.
const referencedIds = (state) =>
  [state.workflow, ...state.past, ...state.future].flatMap((w) => [
    ...w.steps.flatMap((step) => step.inputs || []),
    ...w.outputs.flatMap((output) => (output.sheets || []).map((sheet) => sheet.from)),
  ]);

// Removes a source. Whatever read from it stays pointed at it (shown as not
// connected) until the user links a new source.
const removeSource = (workflow, id) => {
  const pinFirstStep = workflow.sources[0]?.id === id && workflow.steps[0] && !Array.isArray(workflow.steps[0].inputs);
  return {
    ...workflow,
    sources: workflow.sources.filter((s) => s.id !== id),
    steps: pinFirstStep ? [{ ...workflow.steps[0], inputs: [id] }, ...workflow.steps.slice(1)] : workflow.steps,
  };
};

const mapById = (items, id, update) => items.map((item) => (item.id === id ? update(item) : item));

export const editorReducer = (state, action) => {
  const { workflow } = state;
  switch (action.type) {
    case "load":
      return { ...initialState, workflow: action.workflow, files: state.files, selectedId: state.selectedId };

    case "saved":
      return { ...state, workflow: { ...state.workflow, ...action.workflow }, dirty: false };

    case "select":
      return { ...state, selectedId: action.id };

    case "undo":
      if (!state.past.length) return state;
      return {
        ...state,
        workflow: restore(state.past[state.past.length - 1], state.workflow),
        past: state.past.slice(0, -1),
        future: [state.workflow, ...state.future],
        dirty: true,
      };

    case "redo":
      if (!state.future.length) return state;
      return {
        ...state,
        workflow: restore(state.future[0], state.workflow),
        past: [...state.past, state.workflow],
        future: state.future.slice(1),
        dirty: true,
      };

    case "setMeta":
      return change(state, { ...workflow, ...action.changes });

    // ---------- Sources ----------
    // `action.source` was read from the file by the data worker (describeNewSource).
    case "addSource": {
      const id = nextId("source", [...allIds(workflow), ...referencedIds(state)]);
      const source = { ...action.source, id };
      return sourceChange(
        state,
        { ...workflow, sources: [...workflow.sources, source] },
        { files: { ...state.files, [id]: { ...action.file, sheetName: source.sheetName } }, selectedId: id },
      );
    }

    case "updateSource":
      return sourceChange(state, { ...workflow, sources: mapById(workflow.sources, action.id, (s) => ({ ...s, ...action.changes })) });

    // New columns after header settings, the sheet or the file change;
    // `action.source` was re-read by the data worker (reshapeSource in
    // shared/sourceFiles.js), or is missing when there's no file.
    case "reshapeSource": {
      const file = action.file ?? state.files[action.id];
      const files = file ? { ...state.files, [action.id]: { ...file, sheetName: action.settings.sheetName ?? file.sheetName } } : state.files;
      const sources = mapById(workflow.sources, action.id, (s) => (action.source ? { ...action.source, label: s.label } : { ...s, ...action.settings }));
      return sourceChange(state, { ...workflow, sources }, { files });
    }

    // Attaches a sample file to an existing source without changing its
    // columns (the saved columns are what the steps expect). Prefers the
    // sheet the source was built from.
    case "attachSample": {
      const source = workflow.sources.find((s) => s.id === action.id);
      const sheets = action.file.sheets;
      const sheetName =
        action.sheetName ?? (sheets.some((s) => s.name === source?.sheetName) ? source.sheetName : (sheets.find((s) => s.rowCount) || sheets[0])?.name);
      return { ...state, files: { ...state.files, [action.id]: { ...action.file, sheetName, columnOverrides: {} } } };
    }

    // Maps a saved column to another column (by letter) of the uploaded file,
    // e.g. when it was renamed. Kept with the sample file, not saved.
    case "setColumnOverride": {
      const file = state.files[action.id];
      if (!file) return state;
      const columnOverrides = { ...file.columnOverrides, [action.column]: action.letter };
      return { ...state, files: { ...state.files, [action.id]: { ...file, columnOverrides } } };
    }

    case "removeSource": {
      const { [action.id]: _removed, ...files } = state.files;
      return sourceChange(state, removeSource(workflow, action.id), { files, selectedId: null });
    }

    // ---------- Steps ----------
    case "addStep": {
      const id = nextId("step", allIds(workflow));
      const definition = getStepDefinition(action.stepType);
      const step = { id, type: action.stepType, params: defaultParams(action.stepType) };
      const index = action.afterId
        ? workflow.steps.findIndex((s) => s.id === action.afterId) + 1
        : workflow.steps.length;
      const previousId = workflow.steps[index - 1]?.id ?? workflow.sources[0]?.id;
      if (definition.inputs.min > 1) {
        // Multi-input steps start from the previous table plus the next source.
        const other = workflow.sources.find((s) => s.id !== previousId) ?? workflow.sources[0];
        step.inputs = [previousId, other?.id].filter(Boolean);
      } else if (action.inputId) {
        step.inputs = [action.inputId];
      }
      const steps = [...workflow.steps.slice(0, index), step, ...workflow.steps.slice(index)];
      return change(state, { ...workflow, steps }, { selectedId: id });
    }

    case "updateStep":
      return change(state, { ...workflow, steps: mapById(workflow.steps, action.id, (s) => ({ ...s, ...action.changes })) });

    case "updateStepParams":
      return change(state, {
        ...workflow,
        steps: mapById(workflow.steps, action.id, (s) => ({ ...s, params: action.params })),
      });

    case "moveStep": {
      const from = workflow.steps.findIndex((s) => s.id === action.id);
      if (from === -1) return state;
      const steps = [...workflow.steps];
      const [moved] = steps.splice(from, 1);
      steps.splice(Math.max(0, Math.min(action.toIndex, steps.length)), 0, moved);
      return change(state, { ...workflow, steps });
    }

    case "duplicateStep": {
      const index = workflow.steps.findIndex((s) => s.id === action.id);
      const original = workflow.steps[index];
      const id = nextId("step", allIds(workflow));
      const copy = { ...JSON.parse(JSON.stringify(original)), id, inputs: [original.id] };
      const steps = [...workflow.steps.slice(0, index + 1), copy, ...workflow.steps.slice(index + 1)];
      return change(state, { ...workflow, steps }, { selectedId: id });
    }

    case "removeStep":
      return change(state, removeStep(workflow, action.id), { selectedId: null });

    // ---------- Links (trail) ----------
    // Connects a table (source or step) to a step or output. Steps may only
    // read sources or earlier steps.
    case "connect": {
      if (action.outputId) {
        return change(state, {
          ...workflow,
          outputs: mapById(workflow.outputs, action.outputId, (o) => ({
            ...o,
            sheets: o.sheets?.length ? [{ ...o.sheets[0], from: action.fromTable }, ...o.sheets.slice(1)] : [{ name: "Sheet1", from: action.fromTable, writeHeader: true }],
          })),
        });
      }
      const index = workflow.steps.findIndex((s) => s.id === action.stepId);
      const fromIndex = workflow.steps.findIndex((s) => s.id === action.fromTable);
      if (index === -1 || fromIndex >= index) return state;
      const step = workflow.steps[index];
      const { max } = getStepDefinition(step.type).inputs;
      const exists = (id) => workflow.sources.some((s) => s.id === id) || workflow.steps.some((s) => s.id === id);
      let inputs;
      if (step.type === CODE_STEP) {
        // Code is written for a set number of inputs, so a new link replaces
        // one: a missing table or a source without a file first, else the last.
        const all = stepInputs(workflow, index);
        const isEmpty = (id) => !exists(id) || (workflow.sources.some((s) => s.id === id) && !state.files[id]);
        const at = all.some(isEmpty) ? all.findIndex(isEmpty) : all.length - 1;
        inputs = all.length ? [...new Set(all.map((id, i) => (i === at ? action.fromTable : id)))] : [action.fromTable];
      } else {
        const current = stepInputs(workflow, index).filter((id) => id !== action.fromTable && exists(id));
        inputs = max > 1 ? [...current, action.fromTable].slice(-max) : [action.fromTable];
      }
      return change(state, { ...workflow, steps: mapById(workflow.steps, step.id, (s) => ({ ...s, inputs })) });
    }

    case "disconnect": {
      if (action.outputId) {
        return change(state, {
          ...workflow,
          outputs: mapById(workflow.outputs, action.outputId, (o) => ({
            ...o,
            sheets: o.sheets.map((sheet) => (sheet.from === action.tableId ? { ...sheet, from: undefined } : sheet)),
          })),
        });
      }
      const index = workflow.steps.findIndex((s) => s.id === action.stepId);
      const inputs = stepInputs(workflow, index).filter((id) => id !== action.tableId);
      return change(state, { ...workflow, steps: mapById(workflow.steps, action.stepId, (s) => ({ ...s, inputs })) });
    }

    // Custom colour for a link, stored on the receiving step or output;
    // undefined goes back to the colour of the card it comes from.
    case "setLinkColor": {
      const withColor = (item) => {
        const { [action.tableId]: _old, ...rest } = item.linkColors || {};
        const linkColors = action.color ? { ...rest, [action.tableId]: action.color } : rest;
        return { ...item, linkColors: Object.keys(linkColors).length ? linkColors : undefined };
      };
      return action.outputId
        ? change(state, { ...workflow, outputs: mapById(workflow.outputs, action.outputId, withColor) })
        : change(state, { ...workflow, steps: mapById(workflow.steps, action.stepId, withColor) });
    }

    // ---------- Outputs ----------
    case "addOutput": {
      const id = nextId("output", allIds(workflow));
      const from = action.from ?? workflow.steps[workflow.steps.length - 1]?.id ?? workflow.sources[0]?.id;
      const output = {
        id,
        fileName: nextOutputName(workflow.outputs),
        format: "xlsx",
        sheets: [{ name: "Sheet1", from, writeHeader: true }],
      };
      return change(state, { ...workflow, outputs: [...workflow.outputs, output] }, { selectedId: id });
    }

    // A draft from the AI, accepted by the user: one undoable change.
    case "applyDraft":
      return change(
        state,
        { ...workflow, steps: action.steps, outputs: action.outputs, ...(action.description !== undefined ? { description: action.description } : {}) },
        { selectedId: null },
      );

    case "updateOutput":
      return change(state, { ...workflow, outputs: mapById(workflow.outputs, action.id, (o) => ({ ...o, ...action.changes })) });

    case "removeOutput":
      return change(state, { ...workflow, outputs: workflow.outputs.filter((o) => o.id !== action.id) }, { selectedId: null });

    default:
      throw new Error(`Unknown editor action "${action.type}"`);
  }
};
